import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, Button, Chip } from '@mui/material';
import { useTodoData } from 'containers/pages/Todo/hooks/useTodoData';
import { useUsers } from 'containers/pages/Todo/hooks/useUsers';
import { calculateSortOrderAtIndex } from 'containers/pages/Todo/utils/sortOrder';
import { TodoHeader } from 'containers/pages/Todo/components/TodoHeader';
import { KanbanBoard } from 'containers/pages/Todo/components/KanbanBoard';
import { TaskListView } from 'containers/pages/Todo/components/TaskListView';
import { TaskModal } from 'containers/pages/Todo/components/TaskModal';
import { BoardModal } from 'containers/pages/Todo/components/BoardModal';
import ConfirmationDialog from 'components/confirmationDialog/ConfirmationDialog';
import CANtrackCommentDialog from 'containers/pages/Todo/components/CANtrackCommentDialog';
import CompletionDialog from 'containers/pages/Todo/components/CompletionDialog';
import { WorkItem, Column, DragEndResult, ChecklistItem } from 'api/todo/types';
import logo from '../../assets/images/stream.png';

function TodoPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState<'kanban' | 'list'>('kanban');
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [taskModalMode, setTaskModalMode] = useState<'create' | 'edit'>('create');
  const [selectedTask, setSelectedTask] = useState<WorkItem | null>(null);
  const [selectedColumnId, setSelectedColumnId] = useState<number | null>(null);
  const [boardModalOpen, setBoardModalOpen] = useState(false);
  const [boardModalMode, setBoardModalMode] = useState<'create' | 'manage'>('create');
  const [deleteTaskConfirmOpen, setDeleteTaskConfirmOpen] = useState(false);
  const [deleteColumnConfirmOpen, setDeleteColumnConfirmOpen] = useState(false);
  const [columnToDelete, setColumnToDelete] = useState<number | null>(null);
  const [editingColumnId, setEditingColumnId] = useState<number | null>(null);
  const [commentDialogTask, setCommentDialogTask] = useState<WorkItem | null>(null);
  const [completionDialogTask, setCompletionDialogTask] = useState<WorkItem | null>(null);

  const todoData = useTodoData();
  const { users } = useUsers();

  // Sync URL param with board selection
  const boardIdFromUrl = searchParams.get('board');

  // When boards load and URL has a board param, select that board
  useEffect(() => {
    if (todoData.boardsLoading || todoData.boards.length === 0 || !boardIdFromUrl) return;

    const urlBoardId = parseInt(boardIdFromUrl, 10);
    const boardExists = todoData.boards.some((b) => b.board_id === urlBoardId);

    if (boardExists && urlBoardId !== todoData.currentBoard?.board_id) {
      todoData.selectBoard(urlBoardId);
    }
  }, [todoData.boardsLoading, todoData.boards, boardIdFromUrl]);

  // Handle board selection from dropdown - update URL
  const handleSelectBoard = (boardId: number) => {
    todoData.selectBoard(boardId);
    setSearchParams({ board: boardId.toString() }, { replace: true });
  };

  // Handle drag-and-drop
  const handleDragEnd = (result: DragEndResult) => {
    try {
      if (result.type === 'column') {
        // Column reordering
        if (!todoData.currentBoard) {
          console.warn('[handleDragEnd] No current board available for column reorder');
          return;
        }

        const columns = [...todoData.currentBoard.columns].sort((a, b) => a.sort_order - b.sort_order);
        const sourceIndex = columns.findIndex((c) => c.column_id === result.sourceId);
        if (sourceIndex === -1) {
          console.warn('[handleDragEnd] Source column not found:', result.sourceId);
          return;
        }

        const [movedColumn] = columns.splice(sourceIndex, 1);
        const destIndex = result.destinationIndex;
        if (destIndex < 0 || destIndex > columns.length) {
          console.warn('[handleDragEnd] Invalid destination index:', destIndex);
          return;
        }
        
        columns.splice(destIndex, 0, movedColumn);
        const columnOrder = columns.map((col) => col.column_id);

        todoData.reorderColumns(columnOrder).catch((error) => {
          console.error('[handleDragEnd] Failed to reorder columns:', error);
          // Error already handled in hook
        });
      } else if (result.type === 'task') {
        // Task moving
        if (result.sourceColumnId === undefined || result.destinationColumnId === undefined) {
          console.warn('[handleDragEnd] Missing column IDs for task move');
          return;
        }

        const taskId = result.sourceId;
        const destColumnId = result.destinationColumnId;

        // Get tasks in destination column (excluding the moving task), sorted by current sort_order
        const tasksInDestColumn = todoData.workItems
          .filter((t) => t.column_id === destColumnId && t.work_item_id !== taskId)
          .sort((a, b) => a.sort_order - b.sort_order);

        // Calculate proper fractional sort order for the target position
        const destIndex = Math.max(0, Math.min(result.destinationIndex, tasksInDestColumn.length));
        const newSortOrder = calculateSortOrderAtIndex(tasksInDestColumn, destIndex);

        todoData.moveTask(taskId, destColumnId, newSortOrder).catch((error) => {
          console.error('[handleDragEnd] Failed to move task:', error);
          // Error already handled in hook
        });
      }
    } catch (error) {
      console.error('[handleDragEnd] Unexpected error:', error);
    }
  };

  const handleNewTask = () => {
    setTaskModalMode('create');
    setSelectedTask(null);
    setSelectedColumnId(null);
    setTaskModalOpen(true);
  };

  const handleTaskClick = (task: WorkItem) => {
    setTaskModalMode('edit');
    setSelectedTask(task);
    setTaskModalOpen(true);
  };

  const handleAddTask = (columnId: number) => {
    setTaskModalMode('create');
    setSelectedTask(null);
    setSelectedColumnId(columnId);
    setTaskModalOpen(true);
  };

  const handleTaskSave = async (
    title: string,
    description: string,
    columnId: number,
    assigneeIds: number[],
    caseId: number | null,
    caseTitle: string | null,
    dateDue: string | null,
    checklist: ChecklistItem[] | null
  ) => {
    try {
      if (taskModalMode === 'create') {
        // createTask now returns the created task directly - no refresh needed
        const task = await todoData.createTask(columnId, title, description, caseId ?? undefined, caseTitle ?? undefined, dateDue, checklist);
        if (task && assigneeIds.length > 0) {
          // Assign users sequentially to handle errors individually
          for (const userId of assigneeIds) {
            try {
              await todoData.assignUser(task.work_item_id, userId);
            } catch (error) {
              console.error('[handleTaskSave] Failed to assign user:', userId, error);
              // Continue with other assignments even if one fails
            }
          }
        }
        // Close modal on successful create
        setTaskModalOpen(false);
        setSelectedTask(null);
        setSelectedColumnId(null);
      } else if (selectedTask) {
        await todoData.updateTask(selectedTask.work_item_id, title, description, caseId, caseTitle, dateDue, checklist);
        // Update assignees - remove all first, then add selected
        const currentAssigneeIds = selectedTask.assignees.map((a) => a.user_id);

        // Remove assignees that are no longer selected
        for (const userId of currentAssigneeIds) {
          if (!assigneeIds.includes(userId)) {
            try {
              await todoData.removeAssignee(selectedTask.work_item_id, userId);
            } catch (error) {
              console.error('[handleTaskSave] Failed to remove assignee:', userId, error);
              // Continue with other operations
            }
          }
        }

        // Add new assignees
        for (const userId of assigneeIds) {
          if (!currentAssigneeIds.includes(userId)) {
            try {
              await todoData.assignUser(selectedTask.work_item_id, userId);
            } catch (error) {
              console.error('[handleTaskSave] Failed to assign user:', userId, error);
              // Continue with other assignments
            }
          }
        }

        // Move task if column changed
        if (columnId !== selectedTask.column_id) {
          // Calculate proper sort order for the end of the destination column
          const tasksInColumn = todoData.workItems.filter((t) => t.column_id === columnId);
          const sortOrder = calculateSortOrderAtIndex(tasksInColumn, tasksInColumn.length);
          await todoData.moveTask(selectedTask.work_item_id, columnId, sortOrder);
        }
        // Close modal on successful update
        setTaskModalOpen(false);
        setSelectedTask(null);
        setSelectedColumnId(null);
      }
    } catch (error) {
      console.error('[handleTaskSave] Failed:', error);
      // Error already handled in hook, but log for debugging
      // Don't close modal on error so user can retry
    }
  };

  const handleTaskDelete = async () => {
    if (selectedTask) {
      await todoData.deleteTask(selectedTask.work_item_id);
      setDeleteTaskConfirmOpen(false);
      setTaskModalOpen(false);
    }
  };

  const handleCreateBoard = () => {
    setBoardModalMode('create');
    setBoardModalOpen(true);
  };

  const handleManageBoard = () => {
    setBoardModalMode('manage');
    setBoardModalOpen(true);
  };

  const handleBoardSave = async (name: string, isPrivate: boolean) => {
    try {
      if (boardModalMode === 'create') {
        await todoData.createBoard(name, isPrivate);
        setBoardModalOpen(false);
      } else {
        await todoData.updateBoard(name, isPrivate);
        setBoardModalOpen(false);
      }
    } catch (error) {
      // Error already handled in hook
      // Don't close modal on error so user can retry
    }
  };

  const handleColumnMenu = (columnId: number, action: 'rename' | 'delete', newName?: string) => {
    if (action === 'delete') {
      setColumnToDelete(columnId);
      setDeleteColumnConfirmOpen(true);
    } else if (action === 'rename' && newName) {
      todoData.updateColumn(columnId, newName).catch(() => {
        // Error already handled in hook
      });
    }
  };

  const handleDeleteColumn = async () => {
    if (!columnToDelete) return;
    
    try {
      await todoData.deleteColumn(columnToDelete);
      setDeleteColumnConfirmOpen(false);
      setColumnToDelete(null);
    } catch (error) {
      console.error('[handleDeleteColumn] Failed:', error);
      // Error already handled in hook
      // Keep confirmation dialog open on error so user can retry
    }
  };

  const handleAddColumn = async (name: string) => {
    try {
      await todoData.createColumn(name);
    } catch (error) {
      console.error('[handleAddColumn] Failed:', error);
      // Error already handled in hook
    }
  };

  const handleCompleteTask = async (workItemId: number, doneColumnId: number) => {
    // If task has a linked case, open the completion dialog instead of completing directly
    const task = todoData.workItems.find((t) => t.work_item_id === workItemId);
    if (task?.case_id) {
      setCompletionDialogTask(task);
      setTaskModalOpen(false);
      return;
    }

    try {
      // Use the complete endpoint — it handles both standalone and linked tasks.
      // For linked tasks, it moves all instances (primary + references) to their
      // respective boards' done columns.
      await todoData.completeTask(workItemId);
    } catch (error) {
      // Error already handled in hook
    }
  };

  const handleLogToCANtrack = (task: WorkItem) => {
    setCommentDialogTask(task);
  };

  const handleImportTask = async (workItemId: number, targetBoardId: number, targetColumnId: number) => {
    await todoData.importTask(workItemId, targetBoardId, targetColumnId);
  };

  // Loading state
  if (todoData.boardsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Board not found state - URL has board ID that doesn't exist or user doesn't have access
  const urlBoardId = boardIdFromUrl ? parseInt(boardIdFromUrl, 10) : null;
  const boardExistsInList = urlBoardId && todoData.boards.some((b) => b.board_id === urlBoardId);

  if (boardIdFromUrl && !boardExistsInList && todoData.boards.length >= 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          gap: 2,
        }}
      >
        <Typography variant="h5">Board not found</Typography>
        <Typography variant="body2" color="text.secondary">
          This board doesn't exist or you don't have access to it.
        </Typography>
        <Button
          variant="contained"
          onClick={() => setSearchParams({}, { replace: true })}
        >
          Go to Todo
        </Button>
      </Box>
    );
  }

  // Empty state - no boards
  if (!todoData.boardsLoading && todoData.boards.length === 0) {
    return (
      <>
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            minHeight: '95vh' 
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            flex: 1,
            gap: 2 
          }}>
            <Typography variant="h5">No boards yet</Typography>
            <Typography variant="body2" color="text.secondary">
              Create your first board to get started
            </Typography>
            <Button variant="contained" onClick={handleCreateBoard}>
              Create Board
            </Button>
          </Box>
          {/* Footer with Chip */}
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
            <Chip
              label="Powered by Stream Data Systems"
              variant="outlined"
              icon={
                <Box
                  component="img"
                  src={logo}
                  alt="logo"
                  sx={{ width: 20, height: 20 }}
                />
              }
            />
          </Box>
        </Box>
        <BoardModal
          open={boardModalOpen}
          mode={boardModalMode}
          board={todoData.currentBoard || undefined}
          onClose={() => setBoardModalOpen(false)}
          onSave={handleBoardSave}
        />
      </>
    );
  }

  const columns = todoData.currentBoard?.columns || [];
  const sortedColumns = [...columns].sort((a, b) => a.sort_order - b.sort_order);

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        minHeight: '95vh',
      }}
    >
      <Box sx={{ flex: 1 }}>
        <TodoHeader
          boards={todoData.boards}
          currentBoardId={todoData.currentBoard?.board_id || null}
          view={view}
          isOwner={todoData.isOwner}
          users={users}
          filters={todoData.filters}
          onFiltersChange={todoData.setFilters}
          availableCases={todoData.availableCases}
          onSelectBoard={handleSelectBoard}
          onViewChange={setView}
          onNewTask={handleNewTask}
          onCreateBoard={handleCreateBoard}
          onManageBoard={handleManageBoard}
        />

        {todoData.boardLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        ) : !todoData.currentBoard ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <Typography>Select a board to get started</Typography>
          </Box>
        ) : (
          <>
            {view === 'kanban' ? (
              <KanbanBoard
                columns={sortedColumns}
                workItems={todoData.workItems}
                columnTotals={todoData.columnTotals}
                isOwner={todoData.isOwner}
                onDragEnd={handleDragEnd}
                onTaskClick={handleTaskClick}
                onAddTask={handleAddTask}
                onAddColumn={handleAddColumn}
                onColumnMenu={handleColumnMenu}
                onLoadMore={todoData.loadMore}
              />
            ) : (
              <TaskListView
                workItems={todoData.workItems}
                columns={sortedColumns}
                columnTotals={todoData.columnTotals}
                onRowClick={handleTaskClick}
                onCompleteTask={handleCompleteTask}
                onLoadMore={todoData.loadMore}
              />
            )}
          </>
        )}
      </Box>

      {/* Footer with Chip */}
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
        <Chip
          label="Powered by Stream Data Systems"
          variant="outlined"
          icon={
            <Box
              component="img"
              src={logo}
              alt="logo"
              sx={{ width: 20, height: 20 }}
            />
          }
        />
      </Box>

      {/* Modals */}
      <TaskModal
        open={taskModalOpen}
        mode={taskModalMode}
        task={selectedTask || undefined}
        columns={sortedColumns}
        users={users}
        boards={todoData.boards}
        currentBoardId={todoData.currentBoard?.board_id}
        preselectedColumnId={selectedColumnId}
        onClose={() => {
          setTaskModalOpen(false);
          setSelectedTask(null);
          setSelectedColumnId(null);
        }}
        onSave={handleTaskSave}
        onDelete={() => setDeleteTaskConfirmOpen(true)}
        onComplete={handleCompleteTask}
        onImport={handleImportTask}
        onLogToCANtrack={handleLogToCANtrack}
      />

      <BoardModal
        open={boardModalOpen}
        mode={boardModalMode}
        board={todoData.currentBoard || undefined}
        onClose={() => setBoardModalOpen(false)}
        onSave={handleBoardSave}
        onDelete={async () => {
          try {
            await todoData.deleteBoard();
            setBoardModalOpen(false);
          } catch (error) {
            console.error('[handleBoardDelete] Failed:', error);
            // Error already handled in hook
            // Keep modal open on error so user can retry
          }
        }}
      />

      {/* Delete Confirmations */}
      <ConfirmationDialog
        open={deleteTaskConfirmOpen}
        onClose={() => setDeleteTaskConfirmOpen(false)}
        onConfirm={handleTaskDelete}
        title={selectedTask?.primary_work_item_id != null ? 'Remove from Board?' : 'Delete Task?'}
        description={
          selectedTask?.primary_work_item_id != null
            ? `This will remove '${selectedTask?.title}' from this board. The task will still exist on its original board.`
            : `This will permanently delete '${selectedTask?.title}'. This action cannot be undone.`
        }
        confirmText={selectedTask?.primary_work_item_id != null ? 'Remove' : 'Delete'}
      />

      <ConfirmationDialog
        open={deleteColumnConfirmOpen}
        onClose={() => {
          setDeleteColumnConfirmOpen(false);
          setColumnToDelete(null);
        }}
        onConfirm={handleDeleteColumn}
        title="Delete Column?"
        description="This will permanently delete this column. Make sure to move or delete all tasks first."
        confirmText="Delete"
      />

      {/* CANtrack Comment Dialog */}
      {commentDialogTask && commentDialogTask.case_id && (
        <CANtrackCommentDialog
          open={!!commentDialogTask}
          caseId={commentDialogTask.case_id}
          caseTitle={commentDialogTask.case_title || 'Unnamed Case'}
          taskTitle={commentDialogTask.title}
          onClose={() => setCommentDialogTask(null)}
          onSaved={() => setCommentDialogTask(null)}
        />
      )}

      {/* Completion Dialog (for case-linked tasks) */}
      {completionDialogTask && completionDialogTask.case_id && (
        <CompletionDialog
          open={!!completionDialogTask}
          caseId={completionDialogTask.case_id}
          caseTitle={completionDialogTask.case_title || 'Unnamed Case'}
          taskTitle={completionDialogTask.title}
          onClose={() => setCompletionDialogTask(null)}
          onComplete={async () => {
            await todoData.completeTask(completionDialogTask.work_item_id);
          }}
        />
      )}
    </Box>
  );
}

export default TodoPage;
